import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedHTMLMessage, injectIntl, intlShape } from 'react-intl';
import { reduxForm, FormSection, getFormSyncErrors, Form, submit } from 'redux-form';
import _ from 'lodash';
import { FLOW_TYPE, COUNTRY, FORM_ID } from '../../../../framework/App/AppConstants';
import Tooltip from '../../../../shared-components/ToolTip/ToolTip';
import UserTitle from '../../../components/UserTitle/UserTitle';
import FirstName from '../../../../shared-components/NameComponent/FirstName/FirstName';
import LastName from '../../../../shared-components/NameComponent/LastName/LastName';
import PhoneNumber from '../../../components/PhoneGroup/PhoneNumber/PhoneNumber';
import PhoneType from '../../../components/PhoneGroup/PhoneType/PhoneType';
import DateOfBirth from '../../../components/DateOfBirth/DateOfBirth';
import TeslaDateOfBirth from '../../../components/TeslaDateOfBirth/TeslaDateOfBirth';
import CurrentUKResident from '../../../components/CurrentUKResident/CurrentUKResident';
import AddressDisplay from '../../../components/AddressDisplay/AddressDisplay';
import AddressGroup from '../../../components/Address/AddressGroup/AddressGroup';
import RentOrOwn from '../../../components/RentOrOwn/RentOrOwn';
import Accordion from '../../../components/Accordion/Accordion';
import AccordionSection from '../../../components/Accordion/AccordionSection';
import MonthlyIncome from '../../../components/FinancialInfo/MonthlyIncome';
import MonthlyHousing from '../../../components/FinancialInfo/MonthlyHousing';
import MonthlyExpenses from '../../../components/FinancialInfo/MonthlyExpenses';
import EmploymentStatus from '../../../components/FinancialInfo/EmploymentStatus';
import OutlineOfCredit from '../../../components/OutlineOfCredit/OutlineOfCredit';
import PrevAddressCheckbox from '../../../components/PrevAddressCheckbox/PrevAddressCheckbox';
import Col from '../../../../shared-components/VXGrid/Col';
import Row from '../../../../shared-components/VXGrid/Row';
import Button, { BUTTON_STYLE, BUTTON_TYPE } from '../../../../shared-components/Button/Button';

import { getInitialFormValues } from '../../../config/form';
import { PERSONAL_INFO_SECTION, FINANCIAL_INFO_SECTION, OUTLINE_OF_CREDIT, PERSONAL_ELIGIBILITY_SECTION } from './ApplyPageFormConstants';
import { trackTransition } from '../../../../framework/store/FPTI/FPTIActions';
import { submitApplicationApi } from './ApplyPageFormActions';
import { spinnerOpen } from '../../../../framework/components/FullPageSpinner/FullPageSpinnerActions';
import { GB_PRIVACY_STATEMENT_URL, GB_CREDIT_CRAIN_URL } from '../../../config/GBConstants';
import CancelUrl, { COMPONENT_STYLE } from '../../../components/CancelUrl/CancelUrl';
import { showEscapeHatch } from '../../../components/EscapeHatch/EscapeHatchActions';
import './ApplyPageForm.less';
import { scrollToTop } from '../../../../framework/App/SkeletonPage/SkeletonPageActions';
import { isXOFlow } from '../../../../framework/App/AppUtils';

const LOCALE_PREFIX = 'GB/pages/ApplyPage/ApplyPageForm';
const LOCALE_PREFIX_FIRSTNAME = 'GB/components/FirstName/FirstName';
const LOCALE_PREFIX_LASTNAME = 'GB/components/LastName/LastName';
const LOCALE_PREFIX_DOB = 'GB/components/DateOfBirth/DateOfBirth';

export class ApplyPageForm extends Component {
    static propTypes = {
        flow: PropTypes.oneOf(Object.values(FLOW_TYPE)).isRequired,
        country: PropTypes.oneOf(Object.values(COUNTRY)).isRequired,
        showPreviousAddress: PropTypes.bool,
        renderUserInputField: PropTypes.bool,
        initialValues: PropTypes.object.isRequired,
        formValues: PropTypes.object,
        formSyncErrors: PropTypes.object,
        firstName: PropTypes.string,
        addressLine1: PropTypes.string,
        isSoftDecline: PropTypes.bool,
        submitting: PropTypes.bool,

        // Provided from decorated reduxForm() component
        handleSubmit: PropTypes.func.isRequired,

        // Dispatch callback
        onChangeBillingShow: PropTypes.func.isRequired,
        onClickApply: PropTypes.func.isRequired,
        onFormSubmit: PropTypes.func.isRequired,
        showEscapeHatch: PropTypes.func.isRequired,

        // Comes from injectIntl HOC
        intl: intlShape.isRequired,

        // Comes from reduxForm HOC
        dirty: PropTypes.bool // Has form been touched
    };

    constructor(props) {
        super(props);

        this.state = {
            showEligibilityBlock: false,
            showChangeBilling: false,
            showPreviousAddress: _.get(props, `initialValues[${PERSONAL_INFO_SECTION}].oneYearAddress`, false)
                || props.showPreviousAddress
        };
    }

    toggleEligibilityMessage = event => {
        event.preventDefault();
        this.setState({
            showEligibilityBlock: !this.state.showEligibilityBlock
        });
    };

    togglePreviousAddress = () => {
        this.setState({
            showPreviousAddress: !this.state.showPreviousAddress
        });
    };

    onChangeBillingClick = () => {
        this.setState({
            showChangeBilling: !this.state.showChangeBilling
        }, () => {
            if (this.state.showChangeBilling) {
                this.props.onChangeBillingShow();
            }
        });
    };

    handleShowEscapeHatch = () => {
        this.props.showEscapeHatch();
    };

    render() {
        const {
            flow,
            firstName,
            renderUserInputField,
            intl,
            dirty,
            onClickApply,
            handleSubmit,
            isSoftDecline,
            submitting,
            onFormSubmit
        } = this.props;

        const buttonLabel = intl.formatMessage({id: `${LOCALE_PREFIX}.applyForBmlUK.bmlApply`});

        const financialInfoTooltip = (
            <Tooltip
                id="financialInfo"
                text={intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.financialInfoTooltip` })}
            />
        );

        const personalInfoTooltip = (
            <Tooltip
                id="personalInformationHelp"
                text={intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.personalInformationTooltip` })}
            />
        );

        const isPersonalInfoFilled = () => _.isEmpty(_.get(this.props, ['formSyncErrors', PERSONAL_INFO_SECTION]));
        const isFinancialInfoFilled = () => _.isEmpty(_.get(this.props, ['formSyncErrors', FINANCIAL_INFO_SECTION]));

        const isTeslaOrVenice = (flow === FLOW_TYPE.TESLA || flow === FLOW_TYPE.VENICE);
        const teslaGBModifier = isTeslaOrVenice ? 'billing-address-title-gb-tesla' : '';

        const DateOfBirthComponent = isTeslaOrVenice => {
            if (isTeslaOrVenice) {
                return <TeslaDateOfBirth />;
            }
            return <DateOfBirth localePrefix={LOCALE_PREFIX_DOB} />;
        };

        const ternaryUtil = (condition, target, fallback) => condition ? target : fallback || '';

        const rentOrOwnComponentWrap = (
            <Row gutterSize={10}>
                <Col xs={12}>
                    <RentOrOwn />
                </Col>
            </Row>
        );

        return (
            <div className="formWrapper">
                <Form
                    action="apply"
                    id="ApplyPageForm"
                    method="post"
                    className={`proceed bmlApplyHappy${renderUserInputField ? ' showChangeBilling' : ''}`}
                    onSubmit={handleSubmit(onFormSubmit)}
                    autoComplete="off"
                >
                    <div>
                        {(flow !== FLOW_TYPE.VENICE) && (
                            <h2 className="applyPage-headline alpha">
                                <FormattedMessage id={`${LOCALE_PREFIX}.applyForBml.bmlUKApplyHeadline`} />
                            </h2>
                        )}
                        {(isTeslaOrVenice) && (
                            <h1 className="applyPage-greetingName">
                                Hi {firstName}!
                            </h1>
                        )}
                    </div>
                    {
                        <p className="applyPage-subheadline applyPage-greetingMessage">
                            {
                                (flow === FLOW_TYPE.DIRECT_APP || isTeslaOrVenice) &&
                            <FormattedHTMLMessage id={`${LOCALE_PREFIX}.applyForBml.bmlUKApplyHeadlineShortTesla`} /> ||
                            <FormattedMessage id={`${LOCALE_PREFIX}.applyForBml.bmlUKApplyHeadlineShort`} />
                            }
                        </p>
                    }
                    <div className={`personal-eligibility-region${this.state.showEligibilityBlock ? ' is-expanded' : ''}`}>
                        <div className="eligible-tool-tip">
                            <Button
                                data-automation-id="eligibilityToolTip"
                                btnStyle={BUTTON_STYLE.LINK}
                                onClick={this.toggleEligibilityMessage}
                                aria-controls={`${PERSONAL_ELIGIBILITY_SECTION}`}
                                aria-expanded={`${this.state.showEligibilityBlock}`}
                            >
                                <FormattedMessage id={`${LOCALE_PREFIX}.applyForBml.bmlUkApplyEligibleLink`} />
                            </Button>


                        </div>
                        {
                            this.state.showEligibilityBlock && (
                                <div id={`${PERSONAL_ELIGIBILITY_SECTION}`} className="gray">
                                    <p className={ternaryUtil(isTeslaOrVenice, 'eligible-header')}><FormattedMessage id={`${LOCALE_PREFIX}.applyForBml.bmlUkApplyEligibleText`} /></p>
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                id={`${LOCALE_PREFIX}.applyForBml.bmlUkApplyEligibleBullet1`}
                                            />
                                        </li>
                                        <li>
                                            <FormattedHTMLMessage
                                                id={`${LOCALE_PREFIX}.applyForBml.bmlUkApplyEligibleBullet2`}
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                id={`${LOCALE_PREFIX}.applyForBml.bmlUkApplyEligibleBullet3`}
                                            />
                                        </li>
                                    </ul>
                                </div>
                            )
                        }
                    </div>

                    <Accordion initialExpandedIndices={[0]}>
                        <AccordionSection
                            id={PERSONAL_INFO_SECTION}
                            className="personalInfo-wrapper"
                            title={<FormattedMessage id={`${LOCALE_PREFIX}.applyForBmlUK.personalInfoHeadLine`} />}
                            extraTitle={personalInfoTooltip}
                        >
                            <FormSection name={PERSONAL_INFO_SECTION}>
                                <div className="personalInfoSection">
                                    <Row gutterSize={10} className={ternaryUtil(isTeslaOrVenice, 'row-groupInputs')}>
                                        <Col xs={3} className="groupInput groupInput-first personalInfoSection-userTitle">
                                            <UserTitle />
                                        </Col>
                                        <Col xs={4} className="groupInput personalInfoSection-firstName">
                                            <FirstName localePrefix={LOCALE_PREFIX_FIRSTNAME} />
                                        </Col>
                                        <Col xs={5} className="groupInput groupInput-last personalInfoSection-lastName">
                                            <LastName localePrefix={LOCALE_PREFIX_LASTNAME} />
                                        </Col>
                                    </Row>
                                    <Row gutterSize={10} className={ternaryUtil(isTeslaOrVenice, 'row-groupInputs')}>
                                        <Col xs={ternaryUtil(isTeslaOrVenice, 3, 6)} className={ternaryUtil(isTeslaOrVenice, 'groupInput personalInfoSection-phoneType')}>
                                            <PhoneType />
                                        </Col>
                                        <Col xs={ternaryUtil(isTeslaOrVenice, 9, 6)} className={ternaryUtil(isTeslaOrVenice, 'groupInput personalInfoSection-phoneNumber')}>
                                            <PhoneNumber />
                                        </Col>
                                    </Row>
                                    <Row gutterSize={10}>
                                        <Col xs={12}>
                                            {DateOfBirthComponent(isTeslaOrVenice)}
                                        </Col>
                                    </Row>
                                    <Row gutterSize={10}>
                                        <Col xs={12}>
                                            <CurrentUKResident />
                                        </Col>
                                    </Row>
                                    <div id="billingAddressTitle" className="billing-address-title clearfix">
                                        <h4 className={`gamma black pull-left ${teslaGBModifier}`}>
                                            {intl.formatMessage({ id: `${LOCALE_PREFIX}.bmlApplyBillingAddress` })}
                                        </h4>
                                        {
                                            (!this.state.showChangeBilling && this.props.addressLine1 != '') && (
                                                <Button
                                                    data-automation-id="changeAddress"
                                                    btnStyle={BUTTON_STYLE.LINK}
                                                    className="change-link changeBilling pull-right"
                                                    id="changeAddress"
                                                    onClick={this.onChangeBillingClick}
                                                >
                                                    <span className="link-text change-link-text">
                                                        <FormattedMessage id={`${LOCALE_PREFIX}.applyForBml.bmlApplyChange`} />
                                                    </span>
                                                    <span className="change-link-caret" />
                                                </Button>
                                            )
                                        }
                                    </div>
                                    <div
                                        className={
                                            `current-address-form-section ${!(this.state.showChangeBilling || this.props.addressLine1 == ''
                                            || isSoftDecline) && 'hide'}`
                                        }
                                    >
                                        <FormSection name="currentAddress">
                                            <AddressGroup />
                                        </FormSection>
                                    </div>
                                    {
                                        (!(this.state.showChangeBilling || this.props.addressLine1 == '' || isSoftDecline)) &&
                                        (
                                            <Row gutterSize={10}>
                                                <Col xs={12}>
                                                    <AddressDisplay />
                                                </Col>
                                            </Row>
                                        )
                                    }
                                    <div className="previousAddressSection previousAddressSection-info">
                                        {/* GB Tesla has RentOrOwn above PrevAddressCheckbox */}
                                        {isTeslaOrVenice && rentOrOwnComponentWrap}

                                        <Row gutterSize={10}>
                                            <Col xs={12}>
                                                <PrevAddressCheckbox onChange={this.togglePreviousAddress} />
                                            </Col>
                                        </Row>

                                        {!isTeslaOrVenice && rentOrOwnComponentWrap}

                                        {this.state.showPreviousAddress && (
                                            <div>
                                                <Row gutterSize={10}>
                                                    <Col xs={12}>
                                                        <h2 className={`gamma black ${teslaGBModifier}`}>{intl.formatMessage({ id: `${LOCALE_PREFIX}.previousBillingAddress` })}</h2>
                                                    </Col>
                                                </Row>
                                                <FormSection name="previousAddress">
                                                    <AddressGroup />
                                                </FormSection>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </FormSection>
                        </AccordionSection>

                        <AccordionSection
                            id={FINANCIAL_INFO_SECTION}
                            className="financial-info-section"
                            title={intl.formatMessage({id: `${LOCALE_PREFIX}.applyForBmlUK.financialInfoHeadline`})}
                            extraTitle={financialInfoTooltip}
                            conditionToExpand={dirty && !isSoftDecline && isPersonalInfoFilled()}
                        >
                            <FormSection name={FINANCIAL_INFO_SECTION}>
                                <MonthlyIncome />
                                <MonthlyHousing />
                                <MonthlyExpenses />
                                <EmploymentStatus />
                            </FormSection>
                        </AccordionSection>

                        <AccordionSection
                            id={OUTLINE_OF_CREDIT}
                            className="outLineOfCreditRegion"
                            title={intl.formatMessage({id: `${LOCALE_PREFIX}.applyForBmlUK.outlineOfCreditHeadline`})}
                            conditionToExpand={dirty && isPersonalInfoFilled() && isFinancialInfoFilled()}
                        >
                            <p className="secondary">
                                {intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.outlineOfCreditSubHeadline` })}
                            </p>
                            <OutlineOfCredit />
                        </AccordionSection>
                    </Accordion>

                    <div className="bml-esign secondary">
                        <FormattedHTMLMessage
                            id={`${LOCALE_PREFIX}.applyForBmlUK.creditCheck`}
                            values={{ privacyStatementUrl: GB_PRIVACY_STATEMENT_URL, crainUrl: GB_CREDIT_CRAIN_URL }}
                        />
                    </div>
                </Form>

                {/* Keep the submit button html outside of the form to help prevent autosaving values in browser onClick={this.onApplyClick}*/}

                <div className="continue-button-wrapper">
                    <Button
                        data-automation-id="continue"
                        className="bmlApplyContinue"
                        id="bmlApplyContinue"
                        btnType={BUTTON_TYPE.SUBMIT}
                        btnStyle={BUTTON_STYLE.PP_PRIMARY}
                        onClick={onClickApply}
                        disabled={submitting}
                        fullWidth
                    >
                        {buttonLabel}
                    </Button>
                </div>
                <CancelUrl
                    componentType={COMPONENT_STYLE.LINK_BUTTON}
                    customOnClick={this.handleShowEscapeHatch}
                    fptiEvent={{
                        stateName: 'apply_pp_credit_applypage',
                        transitionName: 'cancel_apply_pp_credit_applypage'
                    }}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    initialValues: getInitialFormValues(state),
    formValues: _.get(state, 'form.ppcreditapply.values'),
    formSyncErrors: getFormSyncErrors(FORM_ID)(state),
    flow: _.get(state, 'serverData.flow'),
    country: _.get(state, 'serverData.country'),
    renderUserInputField: state.serverData.renderUserInputField,
    firstName: _.get(state, 'serverData.user.firstName', ''),
    showPreviousAddress: _.get(state, 'serverData.showPreviousAddress', false),
    addressLine1: _.get(state, 'serverData.billingAddress.addressLine1'),
    isSoftDecline: _.get(state, 'serverData.showValidationSoftDecline', false)
});

export const mapDispatchToProps = dispatch => ({
    onClickApply() {
        dispatch(submit(FORM_ID));
    },

    onChangeBillingShow() {
        dispatch(trackTransition('pp_credit_applypage', 'change_billing_address'));
    },

    // handleSubmit will call this function when a form submit is triggered
    // formValues are passed by handleSubmit per api specification
    onFormSubmit(formValues) {
        dispatch(spinnerOpen());
        return dispatch(submitApplicationApi(formValues, LOCALE_PREFIX));
    },
    showEscapeHatch() {
        dispatch(showEscapeHatch());
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(
    reduxForm({
        form: FORM_ID,
        destroyOnUnmount: false,
        forceUnregisteredOnUnmount: true,
        onSubmitFail: (errors, dispatch) => dispatch(scrollToTop())
    })(ApplyPageForm)
));
